import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import _ from 'underscore';
import moment from 'moment';

import * as actions from './actions';
import { Wrapper, Container, Button } from '../../components';
import { Header, HeadingContainer } from '../ChallengeListPage/styles';
import TabSelector from '../../components/TabSelector/tabSelector';
import {
  InputAndSearchSection,
  SelectTestLabel,
  DateLabel,
  InputSection,
  FeedbackContainer,
  FeedbackCard,
  LabelContainer,
  LabelHeading,
  QuestionContainer,
  HorizontalLine,
  AnswerLabel,
  FeedbackCheckbox,
  FeedbackInputCheckbox,
  CanvasContainer,
} from './styles';
import Dropdown from '../../components/Dropdown';
import { userRoles } from 'util/appConstants';
import { useActions } from 'hooks/useActions';

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default React.memo(() => {
  const [feedbacksArray, setFeedbacksArray] = useState([]);
  const [dataSet, setDataSet] = useState([]);
  const [testsList, setTestsList] = useState([]);
  const { userRole } = useSelector((state) => state.auth);

  const date = new Date(),
    year = date.getFullYear(),
    month = date.getMonth();

  const [minDate, setMinDate] = useState(moment(new Date(year, month, 1)).format('YYYY-MM-DD'));
  const [maxDate, setMaxDate] = useState(moment(new Date()).format('YYYY-MM-DD'));
  const [test, setTest] = useState('');

  const { getPublishedTests, getCandidateFeedbacks, updateCandidateFeedback } = useActions(actions);

  useEffect(() => {
    getPublishedTests();
  }, [getPublishedTests]);

  let { tests, feedbacks, visualization, statistic, loadingFeedback } = useSelector((state) => state.candidateFeedback);

  useEffect(() => {
    const mappedTestList = tests.map((test) => {
      test = { ...test };
      test.label = test.name;
      test.value = test._id;
      delete test.name;
      return test;
    });
    setTestsList(mappedTestList);
    if (mappedTestList.length) {
      setTest(mappedTestList[0]._id);
      getCandidateFeedbacks({ minDate, maxDate, test: mappedTestList[0]._id });
    }
  }, [tests]);

  useEffect(() => {
    if (!statistic._id) return;
    let index = _.findIndex(feedbacksArray, (i) => i._id === statistic._id);
    let tempFeedbacks = JSON.parse(JSON.stringify(feedbacksArray));
    tempFeedbacks[index].showFeedback = statistic.showFeedback;
    setFeedbacksArray(tempFeedbacks);
  }, [statistic]);

  useEffect(() => {
    setFeedbacksArray(feedbacks);
  }, [feedbacks]);

  useEffect(() => {
    setDataSet(visualization);
  }, [visualization]);

  const renderVisualization = () => {
    if (!dataSet) return;
    const charts = dataSet.map((value) => {
      let data = {
        labels: _.pluck(value.review, 'value'),
        datasets: [
          {
            backgroundColor: ['#3e95cd', '#8e5ea2', '#3cba9f', '#e8c3b9'],
            data: _.pluck(value.review, 'count'),
          },
        ],
      };
      let options = {
        plugins: {
          title: {
            font: { size: 18 },
            display: true,
            text: value.question,
            position: 'bottom',
          },
          legend: { display: false },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
        },
      };
      return <Bar data={data} options={options}></Bar>;
    });
    return (
      <>
        <CanvasContainer>{charts}</CanvasContainer>
      </>
    );
  };

  const renderFeedbackCard = () => {
    if (loadingFeedback) return <FeedbackContainer>Loading</FeedbackContainer>;
    if (!(feedbacksArray && feedbacksArray.length)) return <FeedbackContainer>No data found</FeedbackContainer>;
    return (
      <FeedbackContainer>
        {feedbacksArray.map((feedback, index) => {
          return (
            <FeedbackCard key={index}>
              <LabelContainer>
                <LabelHeading>Name:&nbsp;</LabelHeading>
                <Label>{feedback.candidateName}</Label>
              </LabelContainer>
              <LabelContainer>
                <LabelHeading>Company:&nbsp;</LabelHeading>
                <Label>{feedback.company}</Label>
              </LabelContainer>
              <LabelContainer>
                <LabelHeading>Test:&nbsp;</LabelHeading>
                <Label>{feedback.testName}</Label>
              </LabelContainer>
              <LabelContainer>
                <LabelHeading>Date:&nbsp;</LabelHeading>
                <Label>{moment(feedback.createdAt).format('DD/MM/YYYY')}</Label>
              </LabelContainer>
              <HorizontalLine />
              {feedback.feedback.map((f, index) => {
                if (index === 3) return null;
                return (
                  <>
                    <QuestionContainer>
                      <LabelHeading>
                        Q{index + 1}. {f.question}&nbsp;
                      </LabelHeading>
                      <AnswerLabel>{f.review || 'No answer given'}</AnswerLabel>
                    </QuestionContainer>
                  </>
                );
              })}
              {feedback?.feedback[3]?.comment && (
                <LabelContainer padding="1rem 0 0 0">
                  <LabelHeading>Comment:&nbsp;</LabelHeading>
                  <Label>{feedback.feedback[3].comment}</Label>
                </LabelContainer>
              )}
              {userRole === userRoles.SUPER_ADMIN && (
                <>
                  <HorizontalLine />
                  <LabelContainer>
                    <LabelHeading>Show feedback to customer:&nbsp;</LabelHeading>
                    <FeedbackCheckbox>
                      <FeedbackInputCheckbox
                        type="checkbox"
                        checked={feedback.showFeedback}
                        onChange={() =>
                          updateCandidateFeedback({ _id: feedback._id, showFeedback: !feedback.showFeedback })
                        }
                      />
                    </FeedbackCheckbox>
                  </LabelContainer>
                </>
              )}
            </FeedbackCard>
          );
        })}
      </FeedbackContainer>
    );
  };

  const renderBottomSection = () => {
    return (
      <TabSelector
        items={[
          { label: 'Feedback', component: renderFeedbackCard() },
          { label: 'Visualization', component: renderVisualization() },
        ]}
      ></TabSelector>
    );
  };

  const renderTopSection = () => {
    return (
      <InputAndSearchSection>
        <InputSection>
          <DateLabel>Start Date: </DateLabel>
          <Input type="date" value={minDate} onChange={(e) => setMinDate(e.target.value)} />
        </InputSection>
        <InputSection>
          <DateLabel>End Date: </DateLabel>
          <Input type="date" value={maxDate} onChange={(e) => setMaxDate(e.target.value)} />
        </InputSection>
        <InputSection>
          <SelectTestLabel>Select Test: </SelectTestLabel>
          <Dropdown
            options={testsList}
            onChange={(returnedValue) => {
              const test = _.findWhere(testsList, { value: returnedValue.value });
              setTest(test._id);
            }}
          />
        </InputSection>
        <InputSection>
          <Button
            padding="5px 15px"
            margin="0"
            primaryBlue
            onClick={() => {
              getCandidateFeedbacks({ minDate, maxDate, test });
            }}
          >
            Search
          </Button>
        </InputSection>
      </InputAndSearchSection>
    );
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100%">
      <Container column>
        <HeadingContainer>
          <Header>Candidate Feedback</Header>
        </HeadingContainer>
        {renderTopSection()}
        {renderBottomSection()}
      </Container>
    </Wrapper>
  );
});
